import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Loader2, ShieldCheck } from "lucide-react";

export default function CheckBenefit() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("phone");
  const [demoOtp, setDemoOtp] = useState("");
  const [requestRef, setRequestRef] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = (secs) => {
    setCooldown(secs);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const requestOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/beneficiaries/check", { phone });
      setRequestRef(data.request_ref || "");
      setMaskedPhone(data.masked_phone || "");
      setDemoOtp(data.demo_otp || "");
      setCode("");
      setStage("otp");
      startCooldown(data.resend_in || 60);
      toast.success("Verification code sent to your registered mobile number.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const sendOtp = async (e) => { e.preventDefault(); await requestOtp(); };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/beneficiaries/verify-otp", { phone, code, request_ref: requestRef });
      login({ ...data.beneficiary, role: "beneficiary" });
      toast.success("Verification successful!");
      navigate("/beneficiary");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="rounded-3xl border border-emerald-200 bg-white p-8 shadow-sm tmf-rise">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="mt-5 font-display font-bold text-2xl text-emerald-950">Check Your TMF Benefit</h1>
          <p className="mt-2 text-sm text-gray-600">Enter your registered phone number. We'll verify with a one-time code sent by SMS.</p>

          {stage === "phone" ? (
            <form onSubmit={sendOtp} className="mt-6 space-y-4">
              <div>
                <Label>Registered Mobile Number</Label>
                <Input data-testid="check-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="080XXXXXXXX" required className="mt-1.5 h-12" />
              </div>
              <Button data-testid="check-send-otp" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send OTP"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-6 space-y-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <span>We sent a 6-digit code to your registered mobile number{maskedPhone ? <> ending <span className="font-semibold">{maskedPhone.slice(-4)}</span></> : ""}. It expires in 5 minutes.</span>
              </div>
              {demoOtp && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> DEMO code: <span data-testid="check-demo-otp" className="font-mono font-bold">{demoOtp}</span>
                </div>
              )}
              <div>
                <Label>Enter OTP</Label>
                <Input data-testid="check-otp-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" required className="mt-1.5 h-12 font-mono tracking-widest text-center text-lg" />
              </div>
              <Button data-testid="check-verify-btn" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify OTP"}
              </Button>
              <button type="button" data-testid="check-resend-otp" disabled={cooldown > 0 || loading} onClick={requestOtp}
                className="w-full text-sm text-emerald-700 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed">
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
              </button>
            </form>
          )}
          <p className="mt-5 text-center text-xs text-gray-500">
            Not registered yet? <button onClick={() => navigate("/register")} className="text-emerald-700 font-semibold">Register here</button>
          </p>
        </div>
      </div>
    </div>
  );
}
